import { ArrowsClockwise, PencilSimple, Trash, MapPin, Paperclip, ListBullets, Notepad, UploadSimple, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { StatusBadge, RecurringBadge } from "./RecordsPane";
import { RecurringPanel } from "./recurring/RecurringPanel";
import { MediaGallery, AttachmentsTable, RelatedTable, Section } from "./DetailParts";
import { MediaDropzone, UploadQueue } from "./MediaDropzone";
import { fmtDate, fmtTime, fmtDateTime } from "./serviceApi";

const Field = ({ label, value, testId }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{label}</div>
    <div className="text-sm text-slate-800 font-medium truncate" data-testid={testId}>{value || <span className="text-slate-300">—</span>}</div>
  </div>
);

export function DetailPane({ detail, loading, onEdit, onDelete, canDelete, onDeleteMedia, onOpenMedia, onOpenRelated, queue, onUploadFiles, onRetryUpload, onMakeRecurring, schedule, onOpenSchedule, canManageRecurring, onScheduleChanged }) {
  if (loading && !detail) return <div className="h-full bg-white p-6 text-sm text-slate-400">Loading…</div>;
  if (!detail) {
    return (
      <div className="h-full bg-white flex items-center justify-center p-6" data-testid="service-pane-detail-empty">
        <div className="text-center text-sm text-slate-500 max-w-xs">
          <FileText size={34} className="mx-auto mb-2 text-slate-300" />
          Select a service entry to see its details, photos, videos and notes.
        </div>
      </div>
    );
  }
  const { record: r, property: p, media, related } = detail;
  return (
    <div className="h-full overflow-y-auto bg-white" data-testid="service-pane-detail">
      <div className="px-5 pt-4 pb-3 border-b border-slate-200 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="detail-title">{r.title}</h2>
            <StatusBadge status={r.status} />
            {(r.recurring_schedule_id || schedule) && <RecurringBadge />}
          </div>
          <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-1">
            <MapPin size={13} /> <span data-testid="detail-address">{p.full_address}</span>{r.unit && <span>· {r.unit}</span>}
          </div>
        </div>
        {!schedule && onMakeRecurring && (
          <Button size="sm" variant="outline" onClick={onMakeRecurring} className="text-orange-700 border-orange-200 hover:bg-orange-50" data-testid="detail-make-recurring-btn"><ArrowsClockwise size={13} className="mr-1" /> Make recurring</Button>
        )}
        <Button size="sm" variant="outline" onClick={onEdit} data-testid="detail-edit-btn"><PencilSimple size={13} className="mr-1" /> Edit</Button>
        {canDelete && (
          <Button size="sm" variant="outline" onClick={onDelete} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="detail-delete-btn"><Trash size={13} className="mr-1" /> Delete</Button>
        )}
      </div>
      <div className="p-5 space-y-6">
        {schedule && <RecurringPanel schedule={schedule} canManage={canManageRecurring} onEdit={() => onOpenSchedule(schedule)} onOpenRecord={(id) => onOpenRelated({ id })} onChanged={onScheduleChanged} />}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3">
          <Field label="Service date" value={`${fmtDate(r.service_date)}${r.service_time ? ` · ${fmtTime(r.service_time)}` : ""}`} testId="detail-date" />
          <Field label="Category" value={r.category_name} testId="detail-category" />
          <Field label="Service person" value={r.service_person_name} testId="detail-person" />
          <Field label="Apartment / unit" value={r.unit} testId="detail-unit" />
          <Field label="Brand / manufacturer" value={r.equipment_brand} testId="detail-brand" />
          <Field label="Model number" value={r.equipment_model} testId="detail-model" />
          <Field label="Serial number" value={r.equipment_serial} testId="detail-serial" />
          <Field label="Created" value={`${fmtDateTime(r.created_at)}${r.created_by?.name ? ` · ${r.created_by.name}` : ""}`} />
          <Field label="Last updated" value={`${fmtDateTime(r.updated_at)}${r.updated_by?.name ? ` · ${r.updated_by.name}` : ""}`} />
        </div>
        <Section icon={<Notepad size={14} />} title="Service notes">
          <div className="text-sm text-slate-700 whitespace-pre-wrap rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5 min-h-[48px]" data-testid="detail-notes">
            {r.notes || <span className="text-slate-400">No notes yet.</span>}
          </div>
        </Section>
        <MediaGallery media={media} onOpen={onOpenMedia} onDelete={onDeleteMedia} />
        <Section icon={<UploadSimple size={14} />} title="Add photos & videos">
          <MediaDropzone testId="detail-dropzone" onFiles={onUploadFiles} />
          <UploadQueue items={queue.items} onRetry={onRetryUpload} />
        </Section>
        <Section icon={<Paperclip size={14} />} title={`Files attached to this entry (${media.length})`}>
          <div className="rounded-md border border-slate-200 overflow-hidden"><AttachmentsTable media={media} onOpen={onOpenMedia} /></div>
        </Section>
        <Section icon={<ListBullets size={14} />} title={`Other related service history at ${p.street} (${related.length})`}>
          <p className="text-[11px] text-slate-500 mb-1.5">Separate service entries (and their files) — not attached to this entry. Matched by recurring series, equipment or category, never by service person alone.</p>
          <div className="rounded-md border border-slate-200 overflow-hidden"><RelatedTable related={related} onOpen={onOpenRelated} /></div>
        </Section>
      </div>
    </div>
  );
}
